import { Button, FormikSelect } from '@pzh-ui/components'
import {
    Form,
    Formik,
    FormikConfig,
    FormikValues,
    useFormikContext,
} from 'formik'
import { toFormikValidationSchema } from 'zod-formik-adapter'

import {
    usePublicationActsGetListActs,
    usePublicationEnvironmentsGetListEnvironments,
    usePublicationTemplatesGetListTemplates,
} from '@/api/fetchers'
import {
    DocumentType,
    ProcedureType,
    Publication,
} from '@/api/fetchers.schemas'
import { LoaderSpinner } from '@/components/Loader'
import { ModalFooter } from '@/components/Modal/Modal'
import { useModuleStatusData } from '@/hooks/useModuleStatusData'
import useModalStore from '@/store/modalStore'
import { PUBLICATION_EDIT_SCHEMA } from '@/validation/publication'
import { useParams } from 'react-router-dom'

interface PublicationFormProps {
    type: 'add' | 'edit'
    submitText?: string
}

const PublicationForm = <TData extends FormikValues>({
    type,
    submitText = 'Publicatie opslaan',
    ...rest
}: PublicationFormProps & FormikConfig<TData>) => {
    const setActiveModal = useModalStore(state => state.setActiveModal)

    return (
        <Formik
            enableReinitialize
            validationSchema={toFormikValidationSchema(PUBLICATION_EDIT_SCHEMA)}
            {...rest}>
            {({ isSubmitting }) => (
                <Form className="flex flex-col gap-4">
                    <Fields type={type} />
                    <ModalFooter>
                        <Button
                            variant="link"
                            onPress={() => setActiveModal(null)}>
                            Annuleren
                        </Button>
                        <Button
                            variant="cta"
                            type="submit"
                            isLoading={isSubmitting}>
                            {submitText}
                        </Button>
                    </ModalFooter>
                </Form>
            )}
        </Formik>
    )
}

const Fields = ({ type }: PublicationFormProps) => {
    const { moduleId } = useParams()
    const { values } = useFormikContext<Publication>()

    const { statusOptions, isFetching: moduleStatusFetching } =
        useModuleStatusData(moduleId)

    const {
        data: publicationTemplateOptions,
        isFetching: publicationTemplatesFetching,
    } = usePublicationTemplatesGetListTemplates(
        {
            limit: 100,
            is_active: true,
            document_type: values.Document_Type as DocumentType,
        },
        {
            query: {
                select: data =>
                    data.results.map(template => ({
                        label: template.Title,
                        value: template.UUID,
                    })),
            },
        }
    )

    const { data: environmentOptions, isFetching: environmentsFetching } =
        usePublicationEnvironmentsGetListEnvironments(
            { limit: 100, is_active: true },
            {
                query: {
                    select: data =>
                        data.results.map(environment => ({
                            label: environment.Title,
                            value: environment.UUID,
                        })),
                    enabled: type === 'edit',
                },
            }
        )

    const { data: publicationActOptions, isFetching: publicationActsFetching } =
        usePublicationActsGetListActs(
            {
                limit: 100,
                is_active: true,
                environment_uuid: values.Environment_UUID,
                document_type: values.Document_Type as DocumentType,
            },
            {
                query: {
                    select: data =>
                        data.results.map(act => ({
                            label: act.Title,
                            value: act.UUID,
                        })),
                    enabled:
                        !!values.Environment_UUID && !!values.Document_Type,
                },
            }
        )

    const documentTypeOptions = Object.entries(DocumentType).map(
        ([, value]) => ({ label: value, value })
    )

    const procedureTypeOptions = Object.entries(ProcedureType).map(
        ([, value]) => ({
            label: value === 'draft' ? 'Ontwerp' : 'Definitief',
            value,
        })
    )

    const isLoading =
        publicationTemplatesFetching ||
        environmentsFetching ||
        publicationActsFetching ||
        moduleStatusFetching

    if (isLoading) {
        return (
            <div className="flex justify-center py-10">
                <LoaderSpinner />
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-4">
            {type === 'edit' && (
                <div className="flex gap-4 [&_>div]:flex-1">
                    <FormikSelect
                        name="Document_Type"
                        label="Instrument"
                        options={documentTypeOptions}
                        disabled
                        required
                    />
                    <FormikSelect
                        name="Environment_UUID"
                        label="Publicatieomgeving"
                        options={environmentOptions}
                        disabled
                        required
                    />
                    <FormikSelect
                        name="Procedure_Type"
                        label="Type"
                        options={procedureTypeOptions}
                        disabled
                        required
                    />
                </div>
            )}
            <div>
                <FormikSelect
                    name="Act_UUID"
                    label="Regeling"
                    options={publicationActOptions}
                    placeholder="Selecteer een regeling"
                    disabled={type === 'edit'}
                    required
                />
            </div>
            <div>
                <FormikSelect
                    name="Template_UUID"
                    label="Publicatie template"
                    options={publicationTemplateOptions}
                    placeholder="Selecteer een publicatie template"
                    required
                />
            </div>
            {type === 'add' && (
                <div>
                    <FormikSelect
                        name="Module_Status_ID"
                        label="Modulestatus"
                        placeholder="Selecteer een modulestatus"
                        options={statusOptions}
                        required
                        styles={{
                            menu: base => ({
                                ...base,
                                position: 'relative',
                                zIndex: 9999,
                                marginTop: 4,
                                boxShadow: 'none',
                            }),
                        }}
                    />
                </div>
            )}
        </div>
    )
}

export default PublicationForm
